/**
 * AboutByNumbers.tsx — Gold Garage Door / About Us · Section 5/10
 * Navy · count-up stat grid (IO trigger). MOBILE: real <style> media queries.
 * 10WEB AI: IO only triggers count-up, NOT a lazy-loader. Keep <style>. Waves intentional.
 */

import { useEffect, useRef, useState } from "react";
const NAVY = "#081438";
const GOLD = "#F5A623";
function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}
function Stat({
  value,
  suffix,
  prefix,
  label,
  active,
  big
}) {
  const v = useCountUp(value, active);
  const display = Number.isInteger(value) ? Math.round(v).toLocaleString() : v.toFixed(1);
  return <div style={{
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    height: "100%"
  }}>
      <div style={{
      fontFamily: "'Plus Jakarta Sans', sans-serif",
      fontWeight: 800,
      fontSize: big ? "clamp(2.6rem, 9vw, 5rem)" : "clamp(1.8rem, 6vw, 2.6rem)",
      color: GOLD,
      lineHeight: 1
    }}>{prefix}{display}{suffix}</div>
      <div style={{
      fontFamily: "'Manrope', sans-serif",
      fontSize: big ? "1rem" : "0.85rem",
      color: "rgba(255,255,255,0.7)",
      marginTop: "0.5rem",
      lineHeight: 1.4,
      fontWeight: 600
    }}>{label}</div>
    </div>;
}
const STATS = [{
  value: 1247,
  suffix: "+",
  label: "Doors fitted & repaired across Sydney",
  big: true
}, {
  value: 217,
  suffix: "+",
  label: "Suburbs serviced"
}, {
  value: 15,
  suffix: "+",
  label: "Years on the tools"
}, {
  value: 5.0,
  label: "Average Google rating"
}, {
  value: 8,
  suffix: "+",
  label: "Door brands serviced"
}];
export default function AboutByNumbers() {
  const [active, setActive] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => entries.forEach(e => {
      if (e.isIntersecting) {
        setActive(true);
        obs.disconnect();
      }
    }), {
      threshold: 0.3
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return <section id="about-numbers" ref={ref} style={{
    background: `radial-gradient(ellipse at 80% 0%, #14254d 0%, ${NAVY} 55%)`,
    position: "relative",
    overflow: "hidden",
    paddingTop: "5.5rem",
    paddingBottom: "5.5rem"
  }}>
      <style>{`
        .abn-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 1.25rem; }
        .abn-big { grid-column: span 3; grid-row: span 2; }
        .abn-tile { grid-column: span 3; }
        @media (max-width: 760px) {
          .abn-grid { grid-template-columns: repeat(2, 1fr); }
          .abn-big { grid-column: 1 / 3; grid-row: auto; }
          .abn-tile { grid-column: span 1; }
          #about-numbers { padding-top: 3.5rem; padding-bottom: 3.5rem; }
        }
      `}</style>

      <div aria-hidden style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      lineHeight: 0,
      transform: "rotate(180deg)"
    }}>
        <svg viewBox="0 0 1440 48" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        display: "block",
        width: "100%",
        height: "48px"
      }}><path d="M0,48 C360,0 1080,0 1440,48 L1440,48 L0,48 Z" fill="#FAF7F1" /></svg>
      </div>

      <svg aria-hidden style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none"
    }}><filter id="grain-an"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter><rect width="100%" height="100%" filter="url(#grain-an)" /></svg>

      <div aria-hidden style={{
      position: "absolute",
      top: "48px",
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />

      <div style={{
      maxWidth: "1150px",
      margin: "0 auto",
      padding: "0 1.5rem",
      position: "relative",
      zIndex: 1
    }}>
        <div style={{
        textAlign: "center",
        maxWidth: "600px",
        margin: "0 auto 3rem"
      }}>
          <span style={{
          fontFamily: "'Manrope', sans-serif",
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.16em",
          color: GOLD,
          textTransform: "uppercase"
        }}>By The Numbers</span>
          <h2 style={{
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800,
          fontSize: "clamp(1.65rem, 6vw, 2.6rem)",
          color: "#FFFFFF",
          lineHeight: 1.15,
          margin: "0.75rem 0 0"
        }}>Fifteen years, counted</h2>
        </div>

        <div className="abn-grid">
          <div className="abn-big" style={{
          background: `linear-gradient(160deg, ${GOLD}14, rgba(255,255,255,0.03))`,
          border: `1px solid ${GOLD}40`,
          borderRadius: "16px",
          padding: "2.5rem",
          position: "relative",
          overflow: "hidden"
        }}>
            <div aria-hidden style={{
            position: "absolute",
            right: "-1.5rem",
            bottom: "-2rem",
            fontSize: "9rem",
            fontWeight: 900,
            color: `${GOLD}0F`,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            lineHeight: 1,
            userSelect: "none"
          }}>GG</div>
            <Stat {...STATS[0]} active={active} />
          </div>
          {STATS.slice(1).map((s, i) => <div key={i} className="abn-tile" style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "16px",
          padding: "1.75rem"
        }}>
              <Stat {...s} active={active} />
            </div>)}
        </div>

        <p style={{
        textAlign: "center",
        fontFamily: "'Manrope', sans-serif",
        fontSize: "0.85rem",
        color: "rgba(255,255,255,0.45)",
        marginTop: "2rem"
      }}>Figures reflect cumulative Gold Garage Door jobs since 2009.</p>
      </div>

      <div aria-hidden style={{
      position: "absolute",
      bottom: "48px",
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg, transparent, ${GOLD} 50%, transparent)`
    }} />
      <div aria-hidden style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 48" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        display: "block",
        width: "100%",
        height: "48px"
      }}><path d="M0,48 C360,0 1080,0 1440,48 L1440,48 L0,48 Z" fill="#FAF7F1" /></svg>
      </div>
    </section>;
}